import { api } from "convex/_generated/api";
import { useAction, useQuery } from "convex/react";
import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import {
  ActionButton,
  DevSection,
  JsonViewer,
  ResultPanel,
} from "./-_dev-components";

type ResultState = {
  data: unknown;
  error: unknown;
  timestamp: number;
  label: string;
};

function NotificationsPlaygroundPage() {
  const [result, setResult] = useState<ResultState | null>(null);

  const notifications = useQuery(api.notifications.queries.index.list, {
    tab: "inbox",
  });

  const checkExpirations = useAction(
    api.notification_action_check_expirations
      .checkDocumentExpirationsController,
  );
  const checkDeadlines = useAction(
    api.notification_action_check_audit_deadlines.checkAuditDeadlines,
  );

  function makeResultHandler(label: string) {
    return (r: { data: unknown; error: unknown; timestamp: number }) =>
      setResult({ ...r, label });
  }

  return (
    <div className="flex h-full overflow-hidden font-mono text-sm">
      {/* Left panel - trigger actions */}
      <div className="flex w-[520px] shrink-0 flex-col border-r border-border overflow-hidden">
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          <div className="space-y-0.5">
            <h2 className="font-semibold">Notification Checks</h2>
            <p className="text-xs text-muted-foreground">
              Manually trigger the cron notification handlers
            </p>
          </div>

          <DevSection title="Cron Jobs">
            <p className="text-xs text-muted-foreground">
              These are the same handlers that run on the daily cron schedule.
            </p>
            <div className="flex flex-wrap gap-2">
              <ActionButton
                label="Check Document Expirations"
                onResult={makeResultHandler("Check Document Expirations")}
                onClick={async () => checkExpirations({})}
              />
              <ActionButton
                label="Check Audit Deadlines"
                onResult={makeResultHandler("Check Audit Deadlines")}
                onClick={async () => checkDeadlines({})}
              />
            </div>
          </DevSection>

          <DevSection title="Current Notifications">
            <p className="text-xs text-muted-foreground">
              {notifications
                ? `${notifications.notifications.length} notifications (${notifications.unreadCount} unread)`
                : "Loading..."}
            </p>
            {notifications && (
              <JsonViewer
                data={notifications.notifications}
                label={`Inbox (${notifications.notifications.length})`}
              />
            )}
          </DevSection>
        </div>

        {/* Sticky result panel */}
        <div className="shrink-0 border-t border-border bg-background p-3">
          {result ? (
            <ResultPanel result={result} />
          ) : (
            <p className="text-xs text-muted-foreground">
              Run an action to see the result here.
            </p>
          )}
        </div>
      </div>

      {/* Right panel - placeholder */}
      <div className="flex flex-1 items-center justify-center text-xs text-muted-foreground">
        Trigger a notification check to create new notifications
      </div>
    </div>
  );
}

export const Route = createFileRoute("/dev/notifications")({
  component: NotificationsPlaygroundPage,
});
